import { DeepPartial } from 'utility-types';
import { getState } from '../state-persistence';
import { InputMap, SavedInputsMap } from './input-map';
import { RouteData } from './route-data';
import { SummitData } from './summit-data';
import { PlaidFailures } from './plaid-failures';
import { StaticExistingAccount, StaticInvestment } from './investment';
import {
  StaticAdditionalCustomer,
  StaticCoTrustee,
  StaticTrust
} from './additional-customers';
import { StaticBeneficiary } from './beneficiary';
import {
  StaticAccountTransferDetails,
  StaticBankAccountReference,
  StaticBankFailure,
  StaticExternalBankAccount
} from './bank-acount';
import { StaticCustodian } from './custodian';
import { StaticCustomer, StaticPrimaryCustomer } from './customer';
import { StaticExistingBankAccount } from './existing-bank-account';
import { StaticRetirementPlanInfo } from './retirement-plans';
import { PurchaseLimits } from './purchase-limits';
import { APP_VERSION } from '../util';

/*
 * @TODO[2022-01-01]: Update to use generic types and functions (if applicable)
 * will allow cleaner code; possible potential for more versatility
 * as we add additional flows with varying data schemas
 */

// should be only data for create application
export type Application = {
  custodian: StaticCustodian;
  customer: StaticPrimaryCustomer;
  trustee: StaticPrimaryCustomer;
  coTrustees: StaticCoTrustee[];
  trust: StaticTrust;
  beneficiaries: StaticBeneficiary;
  jointOwners: StaticCustomer[];
  minor: StaticCustomer;
  externalAccounts: StaticExternalBankAccount[];
  existingAccounts: StaticExistingAccount[];
  newInvestments: StaticInvestment[];
  retirementPlanInfo: StaticRetirementPlanInfo;
  investmentMethod: string;
  profileApplicationID: string;
  toIRAAccountType: string;
  fromIRAAccountType: string;
  iraAccountType: string;
  fundingType: string;
  chargeType: string;
  applicationStatus: string;
  termsIDs: string[];
  governingState: string;
  bankInformationReference: StaticBankAccountReference;
  bankFailureInformation: StaticBankFailure[];
  accountTransferDetails: StaticAccountTransferDetails[];
  fundingMethodType: string;
  eligibleForTaxWithholding: boolean;
};

export type OptionalStaticApplication = DeepPartial<Application>;

// STATE STORE
export type StateStore = {
  app: OptionalStaticApplication;

  // view state
  accountType?: string;
  chargeType?: string;
  existingAgreements?: any;
  existingAccounts?: any;
  existingBankAccounts?: StaticExistingBankAccount[];
  authenticated?: boolean;
  userStateLoaded?: boolean;
  summitData?: SummitData;
  loadedData?: any;
  routeData?: RouteData;
  enabled?: boolean;
  retailEnabled?: boolean;
  prospect?: boolean;
  rpsDirectRollover?: boolean;
  additionalCustomers?: StaticAdditionalCustomer[];
  updateToIClassModalShown?: boolean;

  // @TODO [2020-06-01]: We should have two containers (tempData) (storedData)
  // these should be in storedData
  amount?: number;
  priorYearAmount?: number;
  currentYearAmount?: number;
  purchaseLimits?: PurchaseLimits;
  estimatedRolloverAmount?: number;
  inReviewMode?: boolean;
  toraParticipant?: boolean;
  custodianCustomerServicePhone?: string;
  chosenInvestments?: any;
  termsData?: any;
  rolloverType?: string;
  formerEmployerName?: string;
  // these should be in tempData
  // removed after a new screen is loaded
  residentialAddressValidation?: any;
  mailingAddressValidation?: any;
  mailingAddressDifferent?: boolean;
  fundsInBrokerage?: boolean | null;
  preliquidated?: boolean | null;
  progressStep?: number;
  progressStepText?: string;
  consumerVersion?: string;
  consumerName?: string;
  // skipped beneficiaries data
  incompleteBeneficiaries?: StaticBeneficiary;
  // evy harness
  evyHarnessType?: string;
  // plaid failures
  plaidFailures?: PlaidFailures;
};
export type PartialStateStore = DeepPartial<StateStore>;
export type StateStoreOrVoid = PartialStateStore | void;

// APPLICATION STATE
export class ApplicationState {
  store: StateStore;
  inputs: InputMap;
  chosen?: any;
  result?: any;
  savedInputs?: SavedInputsMap;
}

export const defaultState = (): ApplicationState => {
  return {
    store: {
      app: {},
      consumerVersion: APP_VERSION,
      userStateLoaded: false,
      inReviewMode: false,
      loadedData: {}
    },
    inputs: {}
  };
};

export const getInitialState = (): ApplicationState => {
  return getState() || defaultState();
};
